import _orderBy from "lodash/orderBy";
import moment from 'moment';
import invert from 'invert-color';

import { BaseModel } from '../base-model';
import { IAppointmentComment, IAppointmentTypeCompact } from '.';
import { IAppointmentStatus } from './appointment-status.model';
import { IAppointmentListing, ISuperAppointmentListing, IListAppointmennts } from './appointment-listing.model';
import { IUserListingModel } from '../user';

import { IPersonAutoComplete } from '../autocomplete';
import { IPropertyAutoComplete } from '../property';
import { COLOR_NUMBER } from '../../common/constants';
import { AGENDA_MOMENT_FORMAT } from "../../common/agenda.constant";

const CANCELLED_STATUS_IDS = [3, 6];
export class Color {
	backgroundColor: string;
	borderColor: string;
	textColor: string;

	/**
	 *
	 */
	constructor(typeColorNumber: number, statusColorNumber?: number) {
		typeColorNumber >>>= 0;
		const typeColorResult = this.convertIntToHex(typeColorNumber).next().value;

		if (typeColorResult) {
			const ct = typeColorResult;
			if (statusColorNumber !== undefined) {
				const statusColorResult = this.convertIntToHex(statusColorNumber).next().value;
				if (statusColorResult) {
					const cs = statusColorResult;
					this.backgroundColor = `rgba(${[cs.r, cs.g, cs.b, cs.a].join(',')})`;
					this.borderColor = `rgba(${[ct.r, ct.g, ct.b, ct.a].join(',')})`;
					this.textColor = invert([cs.r, cs.g, cs.b], true);
				} else {
					console.error("convertIntToHex(statusColorNumber) returned undefined.");
				}
			} else {
				this.backgroundColor = `rgba(${[ct.r, ct.g, ct.b, ct.a].join(',')})`;
				this.borderColor = invert([ct.r, ct.g, ct.b]);
				this.textColor = invert([ct.r, ct.g, ct.b], true);
			}
		} else {
			console.error("convertIntToHex(typeColorNumber) returned undefined.");
		}
	}

	static isEmpty(number?: number): boolean {
		return number == null || number == COLOR_NUMBER.CLEAR || number == COLOR_NUMBER.WHITE || number == COLOR_NUMBER.TRANSPARENT;
	}

	* convertIntToHex(number: number) {
		number = number || 0
		number >>>= 0;
		let b = number & 0xFF,
			g = (number & 0xFF00) >>> 8,
			r = (number & 0xFF0000) >>> 16,
			a = ((number & 0xFF000000) >>> 24) / 255;
		if (number === 0)
			b = g = r = 255;
		yield {
			number,
			r,
			g,
			b,
			a: a > 0 ? a - 0.3 : a
		}
	}
}


export interface IEvent {
    isSuper?: boolean;
	url?: string;
	className?: string;
	startEditable?: boolean;
	durationEditable?: boolean;
	resourceEditable?: boolean;
	rendering?: string;
	overlap?: boolean;
	constraint?: any;
	appointmentLayout?: string;
	id?: number;
	originId?: number;
	masterId?: number;
	title?: string;
	allDay?: boolean;
	originAllDay?: boolean;
	start?: any;
	end?: any;
	editable?: boolean;
	color?: string;
	backgroundColor?: string;
	borderColor?: string;
	textColor?: string;
	icon?: string;
	appointmentTypeId?: number;
	indexOf?: number;
	resourceId?: string;
	resourceIds?: string[];
	listOfUsers?: IUserListingModel[];
	resourceIdsOrigin?: string;
	typeInfo?: IAppointmentTypeCompact;
	statusInfo?: IAppointmentStatus;
	isRecurrence?: boolean;
	isException?: boolean;
	isAlarm?: boolean;
	comment?: string;
	listOfComments?: IAppointmentComment[];
	listOfProperties?: IPropertyAutoComplete[];
	listOfPeople?: IPersonAutoComplete[];
	appointmentPlace?: string;
	internalComments?: string;
	listOfPropertyId?: string;
	listOfPersonId?: string;
	originStartTime?: any;
	originEndTime?: any;
	decrement?: number | 0;
	createUserId?: number;
	hasComment?: boolean;
	listAppointmennts?: IListAppointmennts[];
	isAllCancelled?: boolean;
	isThePast?: boolean;
	listOfUserId?: Array<number>;
}

export interface IAppointmentResource {
	id?: number;
	eventColor?: Color;
	eventBackgroundColor?: string;
	eventBorderColor?: string;
	eventTextColor?: string;
	eventClassName?: string;
	businessHours?: string;
	children?: { id: number; title: string };
	parent?: { id: number; title: string };
	title?: string;
	parentId?: number;
}

export class AppointmentResource extends BaseModel<IAppointmentResource> implements IAppointmentResource {
	id: number;
	eventColor?: Color;
	eventBackgroundColor?: string;
	eventBorderColor?: string;
	eventTextColor?: string;
	eventClassName?: string;
	businessHours?: string;
	children?: { id: number; title: string; };
	parent?: { id: number; title: string; };
	title: string;
	parentId?: number;

	/**
	 *
	 */
	constructor(id: number, title: string, options?: IAppointmentResource) {
		super(options);
		this.id = id;
		this.title = title;
	}
}

export class AppointmentEvent implements IEvent {
    isSuper?: boolean;
	url?: string;
	className?: string;
	startEditable?: boolean;
	durationEditable?: boolean;
	resourceEditable?: boolean;
	rendering?: string;
	overlap?: boolean;
	constraint?: any;
	appointmentLayout?: string;
	id: number;
	originId: number;
	masterId: number;
	title: string;
	allDay: boolean;
	originAllDay: boolean;
	start: any;
	end: any;
	editable: boolean;
	color: string;
	backgroundColor: string;
	borderColor: string;
	textColor: string;
	icon: string;
	borderWidth: string;
	borderRadius: string;
	appointmentTypeId: number;
	indexOf: number;
	resourceId: string;
	resourceIds: string[];
	resourceIdsOrigin: string;
	listOfUsers: any;
	resources: any;
	typeInfo: IAppointmentTypeCompact;
	statusInfo: IAppointmentStatus;
	isRecurrence: boolean;
	isException: boolean;
	isAlarm: boolean;
	comment: string;
	listOfComments?: IAppointmentComment[];
	listOfProperties?: IPropertyAutoComplete[];
	listOfPeople?: IPersonAutoComplete[];
	appointmentPlace?: string;
	internalComments?: string;
	listOfPropertyId?: string;
	listOfPersonId?: string;
	isNoBgColor?: boolean; //color of both status & type -> empty
	originStartTime?: any;
	originEndTime?: any;
	decrement?: number | 0;
	createUserId?: number;
	hasComment?: boolean;

	/**
	 *
	 */
	constructor(appointment: IAppointmentListing, index: number, color?: Color) {
        this.isSuper = false;
		this.id = index + 1;
		this.borderWidth = '3px';
		this.borderRadius = '5px';
		this.start = appointment.StartTime;
		this.end = appointment.EndTime;
		this.title = appointment.CombineSubject;
		// this.title = appointment.Subject;
		this.originId = appointment.Id;
		this.appointmentTypeId = appointment.AppointmentTypeId;
		this.indexOf = index;
		
		if (appointment.ListOfUsers) {
            this[appointment.ListOfUsers.length > 1 ? 'resourceIds' : 'resourceId'] = appointment.ListOfUsers.map((item: any) => item.OwnerId) as any;
        }
		this.resourceIdsOrigin = appointment.UserIds;
		this.listOfUsers = appointment.ListOfUsers;
		this.typeInfo = appointment.AppointmentTypeInfo;
		this.statusInfo = appointment.AppointmentStatusInfo;
		this.isRecurrence = appointment.IsRecurrence;
		this.isException = appointment.IsException;
		this.masterId = appointment.MasterId;
		this.isAlarm = appointment.Alarm;
		this.allDay = appointment['IsAllDay'];
		this.originAllDay = appointment['IsAllDay'];
		this.listOfComments = appointment.ListOfComment;
		this.listOfPeople = appointment.ListOfPerson;
		this.listOfProperties = appointment.ListOfProperty;
		this.appointmentPlace = appointment.AppointmentPlace;
		this.internalComments = appointment.InternalComments;
		this.listOfPropertyId = appointment.ListOfPropertyId;
		this.listOfPersonId = appointment.ListOfPersonId;
		if (color != null) this.setColor(color);
		this.originEndTime = appointment.EndTime;
		this.originStartTime = appointment.StartTime;
		this.decrement = 0;
		this.createUserId = appointment.CreatedUser || 0;
		this.hasComment = (appointment.ListOfComment || []).length > 0;
	}

	setColor(color: Color): void {
		this.color = color.backgroundColor;
		this.borderColor = color.borderColor;
		this.textColor = color.textColor;
		this.backgroundColor = color.backgroundColor;
	}

	calculateDecrement() {
		if (this.start === this.end) {
			this.decrement = 1;
			let endTime = moment(this.end).clone().add(1, 's').format(AGENDA_MOMENT_FORMAT);
			this.end = endTime;
		}
	}

	calculateAllDay() {
		let startTime = moment(this.start).clone();
		let endTime = moment(this.end).clone();
		let durationDays = endTime.diff(startTime, 'days');
		if (durationDays && startTime.format('HH:mm') !== '00:00' || durationDays && endTime.format('HH:mm') !== '00:00') {
			this.allDay = true;
			this.originAllDay = true;
			let endTimeExtend = endTime.add(1, "day").format(AGENDA_MOMENT_FORMAT);
			this.end = endTimeExtend;
		} else if (startTime.format('HH:mm') === '00:00' && endTime.format('HH:mm') === '00:00') {
			this.allDay = true;
			this.originAllDay = true;
		}
	}
}

export class SuperAppointmentSuperEvent implements IEvent {
    isSuper?: boolean;
	url?: string;
	className?: string;
	startEditable?: boolean;
	durationEditable?: boolean;
	resourceEditable?: boolean;
	rendering?: string;
	overlap?: boolean;
	constraint?: any;
	appointmentLayout?: string;
	id: number;
	title: string;
	allDay?: boolean;
	originAllDay?: boolean;
	start: any;
	end?: any;
	editable?: boolean;
	color: string;
	backgroundColor: string;
	borderColor: string;
	textColor: string;
	icon?: string;
	borderWidth?: string;
	borderRadius?: string;
	appointmentTypeId?: number;
	indexOf: number;
	resourceId?: string;
	resourceIds?: string[];
	resourceIdsOrigin?: string;
	listOfUsers?: any;
	resources?: any;
	typeInfo?: IAppointmentTypeCompact;
	statusInfo?: IAppointmentStatus;
	isRecurrence?: boolean;
	isException?: boolean;
	isAlarm?: boolean;
	comment?: string;
	listOfComments?: IAppointmentComment[];
	listOfProperties?: IPropertyAutoComplete[];
	listOfPeople?: IPersonAutoComplete[];
	appointmentPlace?: string;
	internalComments?: string;
	listOfPropertyId?: string;
	listOfPersonId?: string;
	isNoBgColor?: boolean; //color of both status & type -> empty
	originStartTime?: any;
	originEndTime?: any;
	decrement?: number | 0;
	createUserId?: number;
    hasComment?: boolean;
    propertyId: number;
    listAppointmennts: IListAppointmennts[];
	isAllCancelled?: boolean;
	isThePast?: boolean;

	/**
	 *
	 */
	constructor(appointment: ISuperAppointmentListing, index: number) {
        this.isSuper = true;
		this.id = parseInt(`${appointment.PropertyId}`+ `${appointment.UserId}` + `${index + 1}`);
		this.borderWidth = '3px';
        this.borderRadius = '5px';
        this.indexOf = index;
        // this.start = appointment.StartTime;
		// this.originStartTime = appointment.StartTime;
        this.title = appointment.CombineSubject;
        this.propertyId = appointment.PropertyId;
		this.resourceId = appointment.UserId.toString();
		
		
		let cancelledList = appointment.ListAppointmennts.filter((e: IListAppointmennts) => e.AppointmentStatusId && CANCELLED_STATUS_IDS.indexOf(e.AppointmentStatusId) > -1);
		this.isAllCancelled = cancelledList.length === appointment.ListAppointmennts.length;

		let startDates = [];
		let endDates = [];

		let listAppointmennts = appointment.ListAppointmennts.map((appt: IListAppointmennts) => {
            if (appt['AppointmentStatusInfo']) appt.statusInfo = appt['AppointmentStatusInfo'];
            if (appt['AppointmentTypeId']) appt.appointmentTypeId = appt['AppointmentTypeId'];

            appt.start = appt.StartTime;
			appt.end = appt.EndTime;
			
			if(!appt.statusInfo || (appt.statusInfo && (this.isAllCancelled || CANCELLED_STATUS_IDS.indexOf(appt.statusInfo.Id) === -1))) {
			// if(appt.statusInfo && (this.isAllCancelled || CANCELLED_STATUS_IDS.indexOf(appt.statusInfo.Id) === -1)) {
				startDates.push(moment(appt.StartTime));
				endDates.push(moment(appt.EndTime));
			}

            appt.originId = appt.Id;
            appt.masterId = appt.Id;
            appt.superPropertyName = appointment.CombineSubject;
            appt.relatedUsers = appt['RelationUserIds'] || [];

            let statusColor = appt.statusInfo !== undefined && appt.statusInfo.Color != null ? appt.statusInfo.Color : COLOR_NUMBER.TRANSPARENT;
            let color = new Color(!Color.isEmpty(statusColor)? statusColor: COLOR_NUMBER.TRANSPARENT);
            appt.color = color.backgroundColor;
            appt.borderColor = color.borderColor;
            appt.textColor = color.textColor;
            appt.backgroundColor = color.backgroundColor;

            return appt;
        });

		let minStartDate = moment.min(startDates);
		this.start = `${minStartDate.format('YYYY-MM-DD[T]HH:mm:ss')}`;
		this.originStartTime = `${minStartDate.format('YYYY-MM-DD[T]HH:mm:ss')}`;

		let maxEndDate = moment.max(endDates);
		this.end = `${maxEndDate.format('YYYY-MM-DD[T]HH:mm:ss')}`;
		this.originEndTime = `${maxEndDate.format('YYYY-MM-DD[T]HH:mm:ss')}`;

		this.isThePast = moment().diff(maxEndDate) > 0;
		this.listAppointmennts = _orderBy(listAppointmennts, ['start'], ['asc'])
		
		this.textColor = "#000000";

		if (this.isAllCancelled && cancelledList.length > 0) {
			this.color = "#ffd4d4";
			this.borderColor = "#0a92da";
			this.backgroundColor = "#ffd4d4";
		} else if (this.isThePast) {
			this.color = "#F2F2F2";
			this.borderColor = "#B5B5B5";
			this.backgroundColor = "#F2F2F2";
		} else {
			this.color = "#e8f4fa";
			this.borderColor = "#0a92da";
			this.backgroundColor = "#e8f4fa";
		}
	}
}